<template>
    <div class="file-manage-container">
        <!-- 顶部工具栏 -->
        <div class="toolbar">
            <div class="toolbar-left">
                <button class="btn btn-primary" @click="openFileSelector">
                    <i class="fas fa-plus"></i> 添加文件
                </button>
                <button class="btn btn-secondary" @click="createFolder">
                    <i class="fas fa-folder-plus"></i> 新建文件夹
                </button>
                <button class="btn btn-success" @click="uploadSelectedFiles"
                    :disabled="selectedFiles.length === 0 || !currentFolder">
                    <i class="fas fa-cloud-upload-alt"></i> 一键上传 ({{ selectedFiles.length }}个文件)
                </button>
                <button class="btn btn-warning" @click="clearSelectedFiles" :disabled="selectedFiles.length === 0">
                    <i class="fas fa-trash-alt"></i> 清空选中
                </button>
            </div>

            <div class="toolbar-right">
                <div class="search-box">
                    <input type="text" v-model="searchQuery" placeholder="搜索文件..." class="search-input">
                    <i class="fas fa-search search-icon"></i>
                </div>
            </div>
        </div>

        <!-- 文件选择隐藏输入框 -->
        <input type="file" ref="fileInput" @change="handleFileSelect" multiple style="display: none;">

        <!-- 主内容区 -->
        <div class="main-content">
            <!-- 左侧目录树 -->
            <div class="sidebar">
                <div class="directory-tree">
                    <h3>文件目录</h3>
                    <div class="tree-container">
                        <!-- 使用 Element Plus 的树形控件 -->
                        <el-tree :data="directoryTree" :props="treeProps" :default-expanded-keys="defaultExpandedKeys"
                            node-key="path" @node-click="onNodeClick" @current-change="onCurrentChange">
                            <template #default="{ node, data }">
                                <div class="custom-tree-node">
                                    <i v-if="data.type === 'folder'"
                                        :class="node.expanded ? 'fas fa-folder-open folder-icon' : 'fas fa-folder folder-icon'"></i>
                                    <i v-else :class="getFileIcon(data.name) + ' file-icon'"></i>
                                    <span class="node-label">{{ node.label }}</span>
                                </div>
                            </template>
                        </el-tree>
                    </div>
                </div>
            </div>

            <!-- 中间文件列表和预览区 -->
            <div class="content-area">
                <!-- 选中的待上传文件列表 -->
                <div class="selected-files-section" v-if="selectedFiles.length > 0">
                    <h3>待上传文件 ({{ selectedFiles.length }}个)</h3>
                    <div class="selected-files-list">
                        <div v-for="(file, index) in selectedFiles" :key="index" class="selected-file-item">
                            <div class="file-info">
                                <i :class="getFileIcon(file.name)"></i>
                                <span class="file-name">{{ file.name }}</span>
                                <span class="file-size">{{ formatFileSize(file.size) }}</span>
                            </div>
                            <div class="file-actions">
                                <div class="progress-bar" v-if="file.uploadProgress > 0 && file.uploadProgress < 100">
                                    <div class="progress-fill" :style="{ width: file.uploadProgress + '%' }"></div>
                                </div>
                                <span v-if="file.uploadProgress === 100" class="upload-complete">已完成</span>
                                <button class="btn-remove" @click="removeSelectedFile(index)"
                                    :disabled="file.uploadProgress > 0">
                                    <i class="fas fa-times"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 文件预览区 -->
                <div class="preview-section">
                    <h3>{{ previewFile ? previewFile.name : '文件预览' }}</h3>
                    <div class="preview-content" v-if="previewFile">
                        <!-- 图片预览 -->
                        <div v-if="previewFile.previewType === 'image'" class="image-preview">
                            <img :src="previewFile.url" :alt="previewFile.name">
                        </div>

                        <!-- 文本预览 -->
                        <div v-else-if="previewFile.previewType === 'text'" class="text-preview">
                            <textarea v-model="previewFile.content" @blur="saveFileContent"
                                :disabled="!previewFile.editable"></textarea>
                            <button v-if="!previewFile.editable" class="btn-edit" @click="enableEditing">
                                编辑
                            </button>
                            <button v-else class="btn-save" @click="saveFileContent">
                                保存
                            </button>
                        </div>

                        <!-- Markdown预览 -->
                        <div v-else-if="previewFile.previewType === 'markdown'" class="markdown-preview">
                            <div v-if="!previewFile.editable" class="markdown-display">
                                <div class="markdown-content" v-html="renderMarkdown(previewFile.content)"></div>
                                <button class="btn-edit" @click="enableEditing">
                                    编辑
                                </button>
                            </div>
                            <div v-else class="markdown-editor">
                                <textarea v-model="previewFile.content"></textarea>
                                <div class="editor-actions">
                                    <button class="btn-save" @click="saveFileContent">
                                        保存
                                    </button>
                                    <button class="btn-cancel" @click="cancelEditing">
                                        取消
                                    </button>
                                </div>
                            </div>
                        </div>

                        <!-- PDF预览 -->
                        <div v-else-if="previewFile.previewType === 'pdf'" class="pdf-preview">
                            <embed :src="previewFile.url" type="application/pdf" style="width:100%;height:100%;">
                        </div>

                        <!-- Word预览 -->
                        <div v-else-if="previewFile.previewType === 'word'" class="word-preview">
                            <p>Word文件预览功能待实现</p>
                            <a :href="previewFile.url" target="_blank">点击下载文件</a>
                        </div>

                        <!-- Excel预览 -->
                        <div v-else-if="previewFile.previewType === 'excel'" class="excel-preview">
                            <p>Excel文件预览功能待实现</p>
                            <a :href="previewFile.url" target="_blank">点击下载文件</a>
                        </div>

                        <!-- PPT预览 -->
                        <div v-else-if="previewFile.previewType === 'ppt'" class="ppt-preview">
                            <p>PPT文件预览功能待实现</p>
                            <a :href="previewFile.url" target="_blank">点击下载文件</a>
                        </div>

                        <!-- 音频预览 -->
                        <div v-else-if="previewFile.previewType === 'audio'" class="audio-preview">
                            <audio :src="previewFile.url" controls style="width:100%;">
                                您的浏览器不支持音频播放。
                            </audio>
                        </div>

                        <!-- 视频预览 -->
                        <div v-else-if="previewFile.previewType === 'video'" class="video-preview">
                            <video :src="previewFile.url" controls style="width:100%;height:100%;">
                                您的浏览器不支持视频播放。
                            </video>
                        </div>

                        <!-- 默认预览 -->
                        <div v-else class="default-preview">
                            <i :class="getFileIcon(previewFile.name)"></i>
                            <p>此文件类型暂不支持预览</p>
                            <a :href="previewFile.url" target="_blank">点击下载文件</a>
                        </div>
                    </div>
                    <div class="preview-placeholder" v-else>
                        <p>请选择文件进行预览</p>
                    </div>
                </div>
            </div>
        </div>

        <!-- 拖拽上传区域 -->
        <div class="drop-zone" @dragover.prevent @dragenter.prevent @drop="handleDrop"
            :class="{ 'drag-over': isDragging }">
            <div class="drop-content">
                <i class="fas fa-cloud-upload-alt"></i>
                <p>拖拽文件到此处上传</p>
                <p class="drop-hint">支持多文件同时上传</p>
            </div>
        </div>

        <!-- 新建文件夹对话框 -->
        <div class="modal" v-if="showCreateFolderModal">
            <div class="modal-content">
                <h3>新建文件夹</h3>
                <input type="text" v-model="newFolderName" placeholder="请输入文件夹名称" class="folder-input">
                <div class="modal-actions">
                    <button class="btn-cancel" @click="cancelCreateFolder">取消</button>
                    <button class="btn-confirm" @click="confirmCreateFolder">确定</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import FileService from '@/services/fileService'
import config from '@/config'
import { ElTree } from 'element-plus'

export default {
    name: 'FileManage',
    components: {
        ElTree
    },
    data() {
        return {
            directoryTree: [],
            treeProps: {
                children: 'children',
                label: 'name',
                isLeaf: (data) => data.type === 'file'
            },
            defaultExpandedKeys: ['/'],
            currentPath: '/',
            currentFolder: null,
            selectedFiles: [],
            previewFile: null,
            searchQuery: '',
            isDragging: false,
            showCreateFolderModal: false,
            newFolderName: '',
            fileTypes: {
                image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'],
                text: ['txt', 'md', 'js', 'css', 'html', 'py', 'json', 'xml'],
                excel: ['xls', 'xlsx'],
                word: ['doc', 'docx'],
                pdf: ['pdf'],
                ppt: ['ppt', 'pptx'],
                audio: ['mp3', 'm4a', 'wav', 'flac'],
                video: ['mp4', 'avi', 'mkv', 'mov']
            }
        }
    },
    async mounted() {
        await this.loadDirectoryTree()
    },
    methods: {
        // 加载目录树
        async loadDirectoryTree() {
            try {
                // 从后端获取目录树数据
                const result = await FileService.getDirectoryTree()
                if (result.status === 200) {
                    this.directoryTree = [result.data]
                    // 默认展开根节点
                    this.defaultExpandedKeys = [result.data.path]
                } else {
                    console.error('获取目录树失败:', result.message)
                    // 如果获取失败，使用模拟数据
                    this.directoryTree = [
                        {
                            name: '根目录',
                            path: '/',
                            type: 'folder',
                            children: [
                                {
                                    name: 'documents',
                                    path: '/documents',
                                    type: 'folder',
                                    children: [
                                        { name: 'resume.pdf', path: '/documents/resume.pdf', type: 'file' },
                                        { name: 'report.docx', path: '/documents/report.docx', type: 'file' }
                                    ]
                                },
                                {
                                    name: 'images',
                                    path: '/images',
                                    type: 'folder',
                                    children: [
                                        { name: 'photo1.jpg', path: '/images/photo1.jpg', type: 'file' },
                                        { name: 'photo2.png', path: '/images/photo2.png', type: 'file' }
                                    ]
                                },
                                { name: 'notes.txt', path: '/notes.txt', type: 'file' }
                            ]
                        }
                    ]
                }
            } catch (error) {
                console.error('加载目录树失败:', error)
                // 如果获取失败，使用模拟数据
                this.directoryTree = [
                    {
                        name: '根目录',
                        path: '/',
                        type: 'folder',
                        children: [
                            {
                                name: 'documents',
                                path: '/documents',
                                type: 'folder',
                                children: [
                                    { name: 'resume.pdf', path: '/documents/resume.pdf', type: 'file' },
                                    { name: 'report.docx', path: '/documents/report.docx', type: 'file' }
                                ]
                            },
                            {
                                name: 'images',
                                path: '/images',
                                type: 'folder',
                                children: [
                                    { name: 'photo1.jpg', path: '/images/photo1.jpg', type: 'file' },
                                    { name: 'photo2.png', path: '/images/photo2.png', type: 'file' }
                                ]
                            },
                            { name: 'notes.txt', path: '/notes.txt', type: 'file' }
                        ]
                    }
                ]
            }
        },

        // 打开文件选择器
        openFileSelector() {
            this.$refs.fileInput.click()
        },

        // 处理文件选择
        handleFileSelect(event) {
            const files = Array.from(event.target.files)
            this.addFilesToList(files)
            // 清空input值以便下次选择相同文件
            event.target.value = ''
        },

        // 处理拖拽上传
        handleDrop(event) {
            event.preventDefault()
            this.isDragging = false
            const files = Array.from(event.dataTransfer.files)
            this.addFilesToList(files)
        },

        // 添加文件到待上传列表
        addFilesToList(files) {
            files.forEach(file => {
                // 为每个文件添加上传进度属性
                file.uploadProgress = 0
                this.selectedFiles.push(file)
            })
        },

        // 移除选中的待上传文件
        removeSelectedFile(index) {
            this.selectedFiles.splice(index, 1)
        },

        // 清空所有选中的待上传文件
        clearSelectedFiles() {
            this.selectedFiles = []
        },

        // 上传选中的文件
        async uploadSelectedFiles() {
            if (!this.currentFolder && this.currentPath === '/') {
                alert('请先选择一个目标文件夹')
                return
            }

            for (let i = 0; i < this.selectedFiles.length; i++) {
                const file = this.selectedFiles[i]
                try {
                    // 创建FormData对象
                    const formData = new FormData()
                    formData.append('file', file)
                    formData.append('name', file.name)
                    formData.append('path', this.currentPath)

                    // 更新进度为开始上传
                    file.uploadProgress = 10

                    // 实际上传文件
                    const result = await FileService.uploadFile(formData)
                    console.log('文件上传成功:', result)

                    // 更新进度为100%
                    file.uploadProgress = 100
                } catch (error) {
                    console.error('文件上传失败:', error)
                    file.uploadProgress = -1 // 表示上传失败
                }
            }
        },

        // 创建文件夹
        createFolder() {
            this.showCreateFolderModal = true
        },

        // 确认创建文件夹
        async confirmCreateFolder() {
            if (!this.newFolderName.trim()) {
                alert('请输入文件夹名称')
                return
            }

            try {
                const data = {
                    path: this.currentPath,
                    name: this.newFolderName,
                    folder_type: 'folder'
                }

                // 调用后端API创建文件夹
                const result = await FileService.addFolder(data)
                console.log('文件夹创建成功:', result)

                // 重新加载目录树
                await this.loadDirectoryTree()

                // 关闭模态框并清空输入
                this.showCreateFolderModal = false
                this.newFolderName = ''
            } catch (error) {
                console.error('文件夹创建失败:', error)
                alert('文件夹创建失败')
            }
        },

        // 取消创建文件夹
        cancelCreateFolder() {
            this.showCreateFolderModal = false
            this.newFolderName = ''
        },

        // 节点点击事件
        onNodeClick(data, node) {
            console.log('节点点击:', data, node)
            if (data.type === 'folder') {
                this.currentPath = data.path
                this.currentFolder = data
            } else {
                this.previewFile = {
                    name: data.name,
                    path: data.path,
                    type: data.type,
                    editable: false,
                    previewType: '', // 添加预览类型字段
                    url: '', // 添加URL字段
                    content: '' // 添加内容字段
                }
                this.loadFilePreview(data)
            }
        },

        // 当前节点变化事件
        onCurrentChange(data, node) {
            // 可以在这里处理当前选中节点的变化
            console.log('当前节点变化:', data, node)
        },

        // 加载文件预览
        async loadFilePreview(fileNode) {
            // 根据文件类型加载不同的预览内容
            if (this.isImageFile(fileNode.name)) {
                // 图片文件预览
                this.previewFile.url = `${config.API_BASE_URL}/extra-files/${fileNode.path}`
                this.previewFile.previewType = 'image'
            } else if (this.isTextFile(fileNode.name)) {
                // 文本文件预览（包括md, txt, py, js, json等）
                try {
                    // 对于md文件，我们需要特殊处理
                    if (this.isMarkdownFile(fileNode.name)) {
                        this.previewFile.previewType = 'markdown'
                    } else {
                        this.previewFile.previewType = 'text'
                    }

                    // 从后端获取实际文件内容
                    const response = await fetch(`${config.API_BASE_URL}/extra-files/${fileNode.path}`)
                    if (response.ok) {
                        this.previewFile.content = await response.text()
                    } else {
                        this.previewFile.content = '无法加载文件内容'
                    }
                    this.previewFile.url = `${config.API_BASE_URL}/extra-files/${fileNode.path}`
                } catch (error) {
                    console.error('加载文件内容失败:', error)
                    this.previewFile.content = '加载文件内容时出错'
                }
            } else if (this.isPdfFile(fileNode.name)) {
                // PDF文件预览
                this.previewFile.url = `${config.API_BASE_URL}/extra-files/${fileNode.path}`
                this.previewFile.previewType = 'pdf'
            } else if (this.isWordFile(fileNode.name)) {
                // Word文件预览
                this.previewFile.url = `${config.API_BASE_URL}/extra-files/${fileNode.path}`
                this.previewFile.previewType = 'word'
            } else if (this.isExcelFile(fileNode.name)) {
                // Excel文件预览
                this.previewFile.url = `${config.API_BASE_URL}/extra-files/${fileNode.path}`
                this.previewFile.previewType = 'excel'
            } else if (this.isPptFile(fileNode.name)) {
                // PPT文件预览
                this.previewFile.url = `${config.API_BASE_URL}/extra-files/${fileNode.path}`
                this.previewFile.previewType = 'ppt'
            } else if (this.isAudioFile(fileNode.name)) {
                // 音频文件预览
                this.previewFile.url = `${config.API_BASE_URL}/extra-files/${fileNode.path}`
                this.previewFile.previewType = 'audio'
            } else if (this.isVideoFile(fileNode.name)) {
                // 视频文件预览
                this.previewFile.url = `${config.API_BASE_URL}/extra-files/${fileNode.path}`
                this.previewFile.previewType = 'video'
            } else {
                // 其他文件类型
                this.previewFile.url = `${config.API_BASE_URL}/extra-files/${fileNode.path}`
                this.previewFile.previewType = 'default'
            }
        },

        // 启用文件编辑
        enableEditing() {
            // 保存原始内容，用于取消编辑时恢复
            this.previewFile.originalContent = this.previewFile.content
            this.previewFile.editable = true
        },

        // 取消编辑
        cancelEditing() {
            // 恢复原始内容
            this.previewFile.content = this.previewFile.originalContent
            this.previewFile.editable = false
        },

        // 保存文件内容
        async saveFileContent() {
            if (!this.previewFile || !this.previewFile.editable) return

            try {
                const data = {
                    path: this.previewFile.path.substring(0, this.previewFile.path.lastIndexOf('/')),
                    name: this.previewFile.name,
                    content: this.previewFile.content
                }

                // 调用后端API更新文件内容
                const result = await FileService.updateFile(data)
                console.log('文件保存成功:', result)

                this.previewFile.editable = false
                alert('文件保存成功')
            } catch (error) {
                console.error('文件保存失败:', error)
                alert('文件保存失败')
            }
        },

        // 渲染Markdown内容
        renderMarkdown(content) {
            // 简单的Markdown渲染实现
            if (!content) return ''

            // 处理标题
            content = content.replace(/^# (.*$)/gim, '<h1>$1</h1>')
            content = content.replace(/^## (.*$)/gim, '<h2>$1</h2>')
            content = content.replace(/^### (.*$)/gim, '<h3>$1</h3>')

            // 处理粗体
            content = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')

            // 处理斜体
            content = content.replace(/\*(.*?)\*/g, '<em>$1</em>')

            // 处理代码块
            content = content.replace(/`(.*?)`/g, '<code>$1</code>')

            // 处理段落
            content = content.replace(/\n\n/g, '</p><p>')
            content = '<p>' + content + '</p>'

            // 处理换行
            content = content.replace(/\n/g, '<br>')

            return content
        },

        // 获取文件图标
        getFileIcon(fileName) {
            const ext = fileName.split('.').pop().toLowerCase()

            if (this.fileTypes.image.includes(ext)) {
                return 'fas fa-file-image'
            } else if (this.fileTypes.text.includes(ext)) {
                return 'fas fa-file-alt'
            } else if (this.fileTypes.excel.includes(ext)) {
                return 'fas fa-file-excel'
            } else if (this.fileTypes.word.includes(ext)) {
                return 'fas fa-file-word'
            } else if (this.fileTypes.pdf.includes(ext)) {
                return 'fas fa-file-pdf'
            } else if (this.fileTypes.ppt.includes(ext)) {
                return 'fas fa-file-powerpoint'
            } else if (this.fileTypes.audio.includes(ext)) {
                return 'fas fa-file-audio'
            } else if (this.fileTypes.video.includes(ext)) {
                return 'fas fa-file-video'
            } else {
                return 'fas fa-file'
            }
        },

        // 判断是否为图片文件
        isImageFile(fileName) {
            const ext = fileName.split('.').pop().toLowerCase()
            return this.fileTypes.image.includes(ext)
        },

        // 判断是否为文本文件
        isTextFile(fileName) {
            const ext = fileName.split('.').pop().toLowerCase()
            return this.fileTypes.text.includes(ext)
        },

        // 判断是否为Excel文件
        isExcelFile(fileName) {
            const ext = fileName.split('.').pop().toLowerCase()
            return this.fileTypes.excel.includes(ext)
        },

        // 判断是否为Word文件
        isWordFile(fileName) {
            const ext = fileName.split('.').pop().toLowerCase()
            return this.fileTypes.word.includes(ext)
        },

        // 判断是否为PDF文件
        isPdfFile(fileName) {
            const ext = fileName.split('.').pop().toLowerCase()
            return this.fileTypes.pdf.includes(ext)
        },

        // 判断是否为PPT文件
        isPptFile(fileName) {
            const ext = fileName.split('.').pop().toLowerCase()
            return this.fileTypes.ppt.includes(ext)
        },

        // 判断是否为音频文件
        isAudioFile(fileName) {
            const ext = fileName.split('.').pop().toLowerCase()
            return this.fileTypes.audio.includes(ext)
        },

        // 判断是否为视频文件
        isVideoFile(fileName) {
            const ext = fileName.split('.').pop().toLowerCase()
            return this.fileTypes.video.includes(ext)
        },

        // 判断是否为md文件
        isMarkdownFile(fileName) {
            const ext = fileName.split('.').pop().toLowerCase()
            return ext === 'md'
        },

        // 格式化文件大小
        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes'
            const k = 1024
            const sizes = ['Bytes', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(k))
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
        }
    }
}
</script>

<style scoped>
.file-manage-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #f5f7fa;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* 顶部工具栏 */
.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 20px;
    background: #ffffff;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid #eaeaea;
}

.toolbar-left {
    display: flex;
    gap: 10px;
}

.toolbar-right {
    display: flex;
    align-items: center;
}

.search-box {
    position: relative;
}

.search-input {
    padding: 8px 12px 8px 35px;
    border: 1px solid #ddd;
    border-radius: 20px;
    outline: none;
    transition: border-color 0.3s;
}

.search-input:focus {
    border-color: #409eff;
}

.search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
}

/* 主内容区 */
.main-content {
    display: flex;
    flex: 1;
    overflow: hidden;
}

/* 侧边栏 */
.sidebar {
    width: 250px;
    background: #ffffff;
    border-right: 1px solid #eaeaea;
    overflow-y: auto;
}

.directory-tree {
    padding: 20px;
}

.directory-tree h3 {
    margin-top: 0;
    color: #333;
    border-bottom: 1px solid #eee;
    padding-bottom: 10px;
}

.tree-container {
    margin-top: 15px;
}

/* 自定义树节点样式 */
.custom-tree-node {
    display: flex;
    align-items: center;
    gap: 8px;
}

.folder-icon,
.file-icon {
    width: 16px;
    text-align: center;
}

.node-label {
    font-size: 14px;
}

/* 内容区域 */
.content-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

/* 待上传文件列表 */
.selected-files-section {
    padding: 20px;
    background: #ffffff;
    border-bottom: 1px solid #eaeaea;
    max-height: 200px;
    overflow-y: auto;
}

.selected-files-section h3 {
    margin-top: 0;
    color: #333;
}

.selected-files-list {
    margin-top: 10px;
}

.selected-file-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    border: 1px solid #eee;
    border-radius: 4px;
    margin-bottom: 8px;
    background: #fafafa;
}

.file-info {
    display: flex;
    align-items: center;
    gap: 10px;
}

.file-name {
    font-weight: 500;
}

.file-size {
    color: #999;
    font-size: 12px;
}

.file-actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

.progress-bar {
    width: 100px;
    height: 6px;
    background: #e0e0e0;
    border-radius: 3px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: #409eff;
    transition: width 0.3s;
}

.upload-complete {
    color: #67c23a;
    font-size: 12px;
}

.btn-remove {
    background: #f56c6c;
    color: white;
    border: none;
    border-radius: 4px;
    width: 24px;
    height: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-remove:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

/* 预览区域 */
.preview-section {
    flex: 1;
    padding: 20px;
    background: #ffffff;
    overflow-y: auto;
}

.preview-section h3 {
    margin-top: 0;
    color: #333;
}

.preview-content {
    height: calc(100% - 40px);
    border: 1px solid #eee;
    border-radius: 4px;
    overflow: hidden;
}

.image-preview {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: 20px;
}

.image-preview img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.text-preview {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.text-preview textarea {
    flex: 1;
    padding: 15px;
    border: none;
    resize: none;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    line-height: 1.5;
}

.text-preview .btn-edit,
.text-preview .btn-save {
    align-self: flex-end;
    margin: 10px;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.btn-edit {
    background: #409eff;
    color: white;
}

.btn-save {
    background: #67c23a;
    color: white;
}

/* Markdown预览 */
.markdown-preview {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.markdown-display {
    flex: 1;
    padding: 15px;
    overflow-y: auto;
}

.markdown-content {
    flex: 1;
    padding: 10px;
}

.markdown-content h1 {
    font-size: 2em;
    border-bottom: 1px solid #eee;
    padding-bottom: 10px;
}

.markdown-content h2 {
    font-size: 1.5em;
    border-bottom: 1px solid #eee;
    padding-bottom: 8px;
}

.markdown-content h3 {
    font-size: 1.25em;
}

.markdown-content p {
    margin: 10px 0;
    line-height: 1.6;
}

.markdown-content code {
    background-color: #f4f4f4;
    padding: 2px 4px;
    border-radius: 3px;
    font-family: 'Courier New', monospace;
}

.markdown-editor {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.markdown-editor textarea {
    flex: 1;
    padding: 15px;
    border: none;
    resize: none;
    font-family: 'Courier New', monospace;
    font-size: 14px;
    line-height: 1.5;
}

.editor-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 10px;
    border-top: 1px solid #eee;
}

.btn-cancel {
    background: #909399;
    color: white;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.pdf-preview {
    height: 100%;
}

/* Word预览 */
.word-preview {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: #999;
}

.word-preview i {
    font-size: 48px;
    margin-bottom: 15px;
    color: #409eff;
}

/* PPT预览 */
.ppt-preview {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: #999;
}

.ppt-preview i {
    font-size: 48px;
    margin-bottom: 15px;
    color: #409eff;
}

/* 音频预览 */
.audio-preview {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: 20px;
}

/* 视频预览 */
.video-preview {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    padding: 20px;
}

/* Excel预览 */
.excel-preview,
.default-preview {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: #999;
}

.excel-preview i,
.default-preview i {
    font-size: 48px;
    margin-bottom: 15px;
    color: #409eff;
}

.preview-placeholder {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: #999;
}

/* 拖拽上传区域 */
.drop-zone {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(64, 158, 255, 0.1);
    border: 2px dashed #409eff;
    display: none;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.drop-zone.drag-over {
    display: flex;
}

.drop-content {
    text-align: center;
    color: #409eff;
}

.drop-content i {
    font-size: 48px;
    margin-bottom: 15px;
}

.drop-hint {
    font-size: 14px;
    color: #999;
}

/* 模态框 */
.modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1001;
}

.modal-content {
    background: white;
    padding: 20px;
    border-radius: 8px;
    min-width: 300px;
}

.modal-content h3 {
    margin-top: 0;
}

.folder-input {
    width: 100%;
    padding: 10px;
    margin: 15px 0;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-sizing: border-box;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

/* 按钮样式 */
.btn {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 14px;
    transition: all 0.3s;
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn-primary {
    background: #409eff;
    color: white;
}

.btn-secondary {
    background: #909399;
    color: white;
}

.btn-success {
    background: #67c23a;
    color: white;
}

.btn-warning {
    background: #e6a23c;
    color: white;
}

.btn-cancel {
    background: #909399;
    color: white;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.btn-confirm {
    background: #409eff;
    color: white;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .main-content {
        flex-direction: column;
    }

    .sidebar {
        width: 100%;
        border-right: none;
        border-bottom: 1px solid #eaeaea;
    }
}
</style>